import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import apiPath from "../../../utils/pathObj";
import { apiGet, apiPut } from "../../../utils/apiFetch";
import GridLayout from "react-grid-layout";
import useToastContext from 'hooks/useToastContext'
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const HiringView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const notification = useToastContext();
  const { id } = location?.state || {};
  const [formData, setFormData] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [layouts, setLayouts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [targetGroup, setTargetGroup] = useState(null);

  // Load saved layouts from localStorage
  useEffect(() => {
    if (id) {
      fetchFormData();
    } else {
      navigate("/forms-manager");
    }
  }, [id]);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1000);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Handle drag start between groups
  const handleDragStart = (e) => {
    const element = e.currentTarget;
    const groupIndex = parseInt(element.getAttribute("data-group-index"));
    const fieldId = element.getAttribute("data-field-id");
    const fieldElement = element.closest(".react-grid-item > div");
    if (fieldElement) {
      e.dataTransfer.setDragImage(fieldElement, 0, 0);
    }
    setDraggedItem({
      groupIndex,
      fieldId,
      element,
    });

    e.dataTransfer.setData("text/plain", `${groupIndex}-${fieldId}`);
    e.dataTransfer.effectAllowed = "move";
  };

  // Handle drag over a potential target group
  const handleDragOver = (e, groupIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedItem && draggedItem.groupIndex !== groupIndex) {
      setTargetGroup(groupIndex);
    }
  };

  // Handle drop to move item to new group
  const handleDrop = (e, targetGroupIndex) => {
    e.preventDefault();

    // Only handle cross-group drops
    if (!draggedItem || draggedItem.groupIndex === targetGroupIndex) {
      setDraggedItem(null);
      setTargetGroup(null);
      return;
    }

    const sourceGroupIndex = draggedItem.groupIndex;
    const fieldId = draggedItem.fieldId;

    const sourceGroup = [...formData.customFields[sourceGroupIndex]];
    const fieldIndex = sourceGroup.findIndex(
      (f) => f._id.toString() === fieldId.toString()
    );

    if (fieldIndex === -1) {
      setDraggedItem(null);
      setTargetGroup(null);
      return;
    }

    const fieldToMove = sourceGroup[fieldIndex];

    setFormData((prev) => {
      const newFields = [...prev.customFields];
      newFields[sourceGroupIndex] = newFields[sourceGroupIndex].filter(
        (f) => f._id.toString() !== fieldId.toString()
      );

      if (!newFields[targetGroupIndex]) {
        newFields[targetGroupIndex] = [];
      }
      newFields[targetGroupIndex] = [
        ...newFields[targetGroupIndex],
        fieldToMove,
      ];

      return {
        ...prev,
        customFields: newFields.filter((group) => group.length > 0),
      };
    });

    setLayouts((prev) => {
      const newLayouts = { ...prev };

      // Remove from source group
      if (newLayouts[sourceGroupIndex]) {
        newLayouts[sourceGroupIndex] = newLayouts[sourceGroupIndex].filter(
          (l) => !l.i.startsWith(`${sourceGroupIndex}-${fieldId}`)
        );
      }

      return newLayouts;
    });

    setDraggedItem(null);
    setTargetGroup(null);
  };

  // Handle drag end (cleanup)
  const handleDragEnd = () => {
    setDraggedItem(null);
    setTargetGroup(null);
  };

  // In the fetchFormData function, modify the layout creation part:
  const fetchFormData = async () => {
    try {
      setIsLoading(true);
      const res = await apiGet(`${apiPath.getFormsById}/${id}`);
      const form = res?.data?.results;

      const values = {};
      const layoutByGroup = {};

      form?.customFields?.forEach((group, groupIndex) => {
        layoutByGroup[groupIndex] = group.map((field, fieldIndex) => {
          // Calculate base height based on field type
          let baseHeight = 1;
          if (field.type === "textEditor") {
            baseHeight = Math.max(1, baseHeight - 1); // Reduce by 2 for textEditor (minimum 1)
          }

          // If field has existing layout, use that but adjust height
          if (field.layout) {
            return {
              i: `${groupIndex}-${fieldIndex}`,
              x: field.layout.x,
              y: field.layout.y,
              w: field.layout.w,
              h: field.type === "textEditor"
                  ? Math.max(1, field.layout.h - 1)
                  : field.layout.h,
            };
          }
          
          // Default layout with adjusted height
          return {
            i: `${groupIndex}-${fieldIndex}`,
            x: 0,
            y: 0,
            w: 4,
            h: baseHeight,
          };
        });

        group.forEach((field, fieldIndex) => {
          const fieldId = field?.id ?? `${groupIndex}-${fieldIndex}`;
          values[fieldId] =
            field?.value || (field?.type === "checkbox" ? [] : "");
        });
      });

      const updatedFormData = { ...form };
      updatedFormData.customFields = form.customFields.map(
        (group, groupIndex) =>
          group.map((field, fieldIndex) => ({
            ...field,
            layout: {
                x: layoutByGroup[groupIndex][fieldIndex].x,
                y: layoutByGroup[groupIndex][fieldIndex].y,
                w: layoutByGroup[groupIndex][fieldIndex].w,
                h: layoutByGroup[groupIndex][fieldIndex].h,
              },
          }))
      );

      setFormData(updatedFormData);
      setFieldValues(values);
      setLayouts(layoutByGroup);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

// Also update the handleLayoutChange function to maintain these height adjustments:
const handleLayoutChange = (newLayout, groupIndex) => {
  setLayouts((prevLayouts) => {
    const updatedLayouts = { ...prevLayouts, [groupIndex]: newLayout };

    // Add layout data into formData.customFields
    if (formData?.customFields?.[groupIndex]) {
      const updatedFields = formData.customFields[groupIndex].map(
        (field, fieldIndex) => {
          const layoutItem = newLayout[fieldIndex];
          let adjustedHeight = layoutItem?.h ?? 1;
          
          // Apply height adjustments based on field type
          if (field.type === "textEditor") {
            adjustedHeight = Math.max(1, (layoutItem?.h ?? 1) - 1);
          }

          return {
            ...field,
            layout: {
              x: layoutItem?.x ?? 0,
              y: layoutItem?.y ?? 0,
              w: layoutItem?.w ?? 4,
              h: adjustedHeight,
            },
          };
        }
      );

      const updatedFormData = { ...formData };
      updatedFormData.customFields[groupIndex] = updatedFields;
      setFormData(updatedFormData);
    }

    return updatedLayouts;
  });
};

  const handleChange = (e, fieldId, type) => {
    const { value, checked, files } = e.target;

    setFieldValues((prev) => {
      if (type === "checkbox") {
        const current = prev[fieldId] || [];
        return {
          ...prev,
          [fieldId]: checked
            ? [...current, value]
            : current.filter((v) => v !== value),
        };
      }

      if (type === "file") {
        return { ...prev, [fieldId]: files[0] };
      }

      return { ...prev, [fieldId]: value };
    });
  };

  const handleUpdateForm = async () => {
  if (!formData || !formData._id) return;

  const updatedFormData = { ...formData };
  updatedFormData.customFields = formData.customFields.map(
    (group, groupIndex) =>
      group.map((field, fieldIndex) => {
        const fieldId = field?.id ?? `${groupIndex}-${fieldIndex}`;
        const FieldType = field?.type || "text";
        const layoutKey = `${groupIndex}-${fieldIndex}`;
        const layoutItem = layouts[groupIndex]?.find(
          (item) => item.i === layoutKey
        );

        // Remove temporary height adjustments before saving
        let originalHeight = layoutItem?.h || field.layout?.h || 1;
        if (FieldType === "textEditor") {
          originalHeight = originalHeight + 1; // Remove the -1 adjustment
        }

        return {
          ...field,
          value: FieldType === "textEditor" ? fieldValues[fieldId] : "",
          layout: {
            x: layoutItem?.x || field.layout?.x || 0,
            y: layoutItem?.y || field.layout?.y || 0,
            w: layoutItem?.w || field.layout?.w || 4,
            h: originalHeight, // Store the original height without adjustments
          },
        };
      })
  );

  const payload = {
    formId: updatedFormData._id,
    formName: updatedFormData.formName,
    formType: updatedFormData.formType,
    customFields: updatedFormData.customFields,
  };

  try {
    const res = await apiPut(apiPath.updateForm, payload);
    localStorage.removeItem(`layouts-${formData._id}`);
    setLayouts({});
    notification.success(res?.data?.message || "Form updated successfully!");
    navigate("/forms-manager/view");
  } catch (error) {
    console.error("Error updating form:", error);
    notification.error(error?.res?.data?.message || 'Update failed');
  }
};

  const renderField = (field, groupIndex, fieldIndex) => {
    const fieldId = field?.id ?? `${groupIndex}-${fieldIndex}`;
    const value = fieldValues[fieldId] ?? "";
    
    const { type, label, options, placeholder, mandatory, boldText, styles } = field;
    const labelStyle = "text-sm whitespace text-black font-medium mr-[5px]";
    const inputStyle = "flex-1 border px-3 py-2 bg-gray-200 text-xs";

    const baseLabelStyle = "text-sm mr-2";
    const boldStyle = boldText ? "font-bold" : "font-normal";
    const requiredIndicator = mandatory ? <span className="text-red-500 ml-1">*</span> : null;

    switch (type) {
      case "text":
        return (
          <div className={`flex items-center ${styles?.container || ""}`} key={fieldId}>
            <label className={`${baseLabelStyle} ${boldStyle}`}>
              {label}
              {requiredIndicator}
            </label>
          </div>
        );

      case "textarea":
        return (
          <div className={`flex flex-col ${styles?.container || ""}`} key={fieldId}>
            <label className={`${baseLabelStyle} ${boldStyle}`}>
              {label}
              {requiredIndicator}
            </label>
            <textarea
              className="border border-gray-300 rounded-md shadow-sm p-2"
              placeholder={placeholder || ""}
              value={value}
              onChange={(e) => handleChange(e, fieldId, type)}
            />
          </div>
        );

      case "heading":
        return (
          <div className={`w-full ${styles?.container || ""}`} key={fieldId}>
            <h2 className={`text-xl ${boldStyle}`}>
              {label}
              {requiredIndicator}
            </h2>
          </div>
        );

      case "input":
      case "select":
      case "date":
        return (
          <div className={styles.optionsContainer} key={fieldId}>
            <label className={`text-xs ${labelStyle}`}>
              {label}{" "}
              {mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            {type === "input" && (
              <input
                type="text"
                className={inputStyle}
                value={value}
                onChange={(e) => handleChange(e, fieldId, type)}
                placeholder={placeholder}
              />
            )}
            {type === "date" && (
              <input
                type="date"
                className={`${inputStyle || ''} non-draggable`}
                value={value}
                onChange={(e) => handleChange(e, fieldId, type)}
              />
            )}
            {type === "select" && (
              <select
                className={inputStyle}
                value={value}
                onChange={(e) => handleChange(e, fieldId, type)}
              >
                <option value="">Select</option>
                {options?.map((opt, idx) => (
                  <option key={idx} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}
          </div>
        );
      case "textEditor":
        return (
          <div className="mb-4" key={fieldId} style={{ clear: "both" }}>
            <div
              className="bg-white form-view inner-html"
              dangerouslySetInnerHTML={{ __html: value }}
            />
          </div>
        );

      case "radio":
        return (
          <div className={styles.optionsContainer} key={fieldId}>
            <label className="block mb-1 font-bold">
              {label}{" "}
              {mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className=" non-draggable flex gap-4 flex-wrap">
              {options?.map((opt, idx) => (
                <label key={idx} className="non-draggable flex items-center gap-2 ">
                  <input
                    type="radio"
                    name={fieldId}
                    className="non-draggable"
                    value={opt}
                    checked={value === opt}
                    onChange={(e) => handleChange(e, fieldId, type)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        );
      case "checkbox":
        return (
          <div className={styles.optionsContainer} key={fieldId}>
            <label className="block mb-1 font-bold">
              {label}{" "}
              {mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex gap-4 flex-wrap">
              {options?.map((opt, idx) => (
                <label key={idx} className="non-draggable flex items-center gap-2">
                  <input
                    type="checkbox"
                    name={`${fieldId}-${opt}`}
                    value={opt}
                    checked={(value || []).includes(opt)}
                    onChange={(e) => handleChange(e, fieldId, type)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        );
      case "file":
        return (
          <div className="mb-4" key={fieldId}>
            <label className="block mb-1">
              {label}{" "}
              {mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            {value ? (
              <img
                src={value}
                alt="Uploaded image"
                className="max-w-xs h-auto rounded border"
              />
            ) : (
              <p className="text-gray-500 text-sm">No file uploaded</p>
            )}
          </div>
        );
      case "html":
        return (
          <div
            key={fieldId}
            className="mb-4 inner-html"
            dangerouslySetInnerHTML={{ __html: label }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-[#F9F9F9]">
      <div className="py-4 px-3">
        <div className="bg-white border border-[#E9EDF9] rounded-lg">
          <div className="p-6 w-full" ref={containerRef}>
            {formData?.customFields?.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className="relative"
                onDragOver={(e) => handleDragOver(e, groupIndex)}
                onDrop={(e) => handleDrop(e, groupIndex)}
                style={{
                  border:
                    targetGroup === groupIndex ? "1px dashed #3182ce" : "",
                  backgroundColor: targetGroup === groupIndex ? "#ebf8ff" : "",
                }}
              >
                <GridLayout
                  className="layout mb-3"
                  cols={12}
                  rowHeight={20}
                  margin={[4, 4]}
                  style={{ fontSize: "12px" }}
                  width={containerWidth}
                  layout={layouts[groupIndex]}
                  draggableCancel=".non-draggable, .ql-toolbar, .ql-container, .cross-group-drag-handle"
                  isBounded={false}
                  compactType={null}
                  preventCollision={false}
                  useCSSTransforms={false}
                  autoSize={true}
                  isDraggable={!isEditing}
                  isResizable={!isEditing}
                  onLayoutChange={(newLayout) =>
                    handleLayoutChange(newLayout, groupIndex)
                  }
                >
                  {group.map((field, fieldIndex) => (
                    <div
                      key={`${groupIndex}-${fieldIndex}`}
                      data-grid={
                        layouts[groupIndex]?.[fieldIndex] ?? {
                          i: `${groupIndex}-${fieldIndex}`,
                          x: 0,
                          y: 0,
                          w: 4,
                          h: 1,
                        }
                      }
                      // style={{ zIndex: 100 }}
                    >
                      <div className="mb-1 bg-white">
                        <div className="flex justify-start gap-1 items-start p-1">
                          <div
                            className="cross-group-drag-handle cursor-grab"
                            draggable
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            data-group-index={groupIndex}
                            data-field-id={field._id || `${groupIndex}-${fieldIndex}`}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            ⠿
                          </div>
                          <div className="w-full">
                            {renderField(field, groupIndex, fieldIndex)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </GridLayout>
              </div>
            ))}

            <div className="border-t pt-4 flex justify-center">
              <button
                onClick={() => navigate("/forms-manager")}
                className="bg-[#E1E1E1] px-12 py-2.5 rounded text-sm text-black"
              >
                {t("O_BACK")}
              </button>
              <button
                onClick={handleUpdateForm}
                className="ml-4 bg-[#007BFF] text-white px-8 py-2.5 rounded text-sm flex justify-center"
              >
                {t("SAVE")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HiringView;